// 망각의 지뢰 (Oblivion Mine) engine types.
// Rules: docs/wireframes/oblivion-mine/rule.md (2026-06-20 design-confirmed).
// 11x11 board; each player commits 15 hidden mines, then races a single piece
// to treasures. Stepping onto an opponent's ARMED mine reveals+disarms it and
// knocks the piece back to its start (turn lost). First to collect 2 of 3
// treasures wins. Hidden information is asymmetric: a player sees their own
// mines; an opponent's armed mine stays hidden until triggered.

export const OBLIVION_MINE_BOARD_SIZE = 11;
export const OBLIVION_MINE_MINES_PER_PLAYER = 15;
/** Treasures needed to win (of the 3 on the board). */
export const OBLIVION_MINE_TREASURES_TO_WIN = 2;
/** p1 = black (moves first), p2 = white. */
export const OBLIVION_MINE_PLAYERS = ["p1", "p2"] as const;

export type OblivionMinePlayerId = (typeof OBLIVION_MINE_PLAYERS)[number];
export type OblivionMinePhase = "lay-mines" | "move" | "game-over";

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Record<string, unknown>;
}

export interface OblivionMinePosition {
  readonly row: number;
  readonly col: number;
}

/** Opposite corners; mines may not be laid on either start cell. */
export const OBLIVION_MINE_STARTS: Readonly<
  Record<OblivionMinePlayerId, OblivionMinePosition>
> = {
  p1: { row: 0, col: 0 },
  p2: { row: 10, col: 10 },
};

/** Center + the two diagonally-opposite corners (180°-symmetric, fair). */
export const OBLIVION_MINE_TREASURE_CELLS: readonly OblivionMinePosition[] = [
  { row: 5, col: 5 },
  { row: 0, col: 10 },
  { row: 10, col: 0 },
];

export interface OblivionMinePlayer {
  readonly playerId: OblivionMinePlayerId;
  readonly label: string;
}

export interface OblivionMineMine {
  readonly owner: OblivionMinePlayerId;
  readonly row: number;
  readonly col: number;
  /** Revealed + disarmed after an opponent steps on it. */
  readonly triggered: boolean;
}

export interface OblivionMineTreasure {
  readonly row: number;
  readonly col: number;
  readonly collectedBy: OblivionMinePlayerId | null;
}

/** Record of the most recent hit (drives HitFx + recap). */
export interface OblivionMineHit {
  /** The player whose piece stepped on the mine. */
  readonly playerId: OblivionMinePlayerId;
  readonly row: number;
  readonly col: number;
  /** The owner of the triggered mine (always the opponent). */
  readonly owner: OblivionMinePlayerId;
}

export interface LayMinesAction {
  readonly type: "LayMines";
  readonly playerId: OblivionMinePlayerId;
  /** Exactly OBLIVION_MINE_MINES_PER_PLAYER distinct cells (no start/treasure). */
  readonly cells: readonly OblivionMinePosition[];
}

export interface MoveAction {
  readonly type: "Move";
  readonly playerId: OblivionMinePlayerId;
  /** One orthogonal step from the player's current position. */
  readonly to: OblivionMinePosition;
}

export type OblivionMineAction = LayMinesAction | MoveAction;

export interface OblivionMineState {
  readonly gameId: string;
  readonly players: readonly OblivionMinePlayer[];
  readonly phase: OblivionMinePhase;
  /** Authoritative mines for both players (server-only truth). */
  readonly mines: readonly OblivionMineMine[];
  readonly positions: Readonly<
    Record<OblivionMinePlayerId, OblivionMinePosition>
  >;
  readonly treasures: readonly OblivionMineTreasure[];
  /** Whether each player has committed their mines (lay-mines phase). */
  readonly minesLaid: Readonly<Record<OblivionMinePlayerId, boolean>>;
  readonly scores: Readonly<Record<OblivionMinePlayerId, number>>;
  readonly firstPlayerId: OblivionMinePlayerId;
  readonly currentPlayerId: OblivionMinePlayerId;
  readonly moveNumber: number;
  readonly lastHit: OblivionMineHit | null;
  readonly winnerId: OblivionMinePlayerId | null;
  readonly lastAction: OblivionMineAction | null;
}

export interface InitGameOptions {
  readonly gameId?: string;
  readonly firstPlayerId?: OblivionMinePlayerId;
}

// Viewer projection: only mines visible to the viewer are present. Own mines
// and any triggered mine are visible; an opponent's armed mine is omitted
// entirely (never reaches the client). Same mine shape — the redaction is the
// absence of hidden entries, not a masked field.
export interface OblivionMineViewerState
  extends Omit<OblivionMineState, "mines"> {
  readonly mines: readonly OblivionMineMine[];
}
